import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMe } from "@chotca/api-client";
import { BootSplash } from "@chotca/app-shell";
import { WEB_APEX_URL } from "@/env";

type Role = "ADMIN" | "EMPLOYER";

export function AuthGate({
  role,
  children,
}: {
  role: Role;
  children: (
    me: NonNullable<ReturnType<typeof useMe>["data"]>,
  ) => React.ReactNode;
}) {
  const nav = useNavigate();
  const loc = useLocation();
  const { data: me, isPending, isError } = useMe();

  React.useEffect(() => {
    if (isPending) return;
    if (isError || !me) {
      const back = loc.pathname + loc.search;
      nav(`/login?redirect=${encodeURIComponent(back)}`, { replace: true });
      return;
    }
    if (role === "ADMIN" && me.globalRole !== "ADMIN") {
      window.location.assign(`${WEB_APEX_URL}/`);
      return;
    }
    if (
      role === "EMPLOYER" &&
      me.globalRole !== "ADMIN" &&
      me.memberships.length === 0
    ) {
      nav("/onboarding/employer", { replace: true });
    }
  }, [isPending, isError, me, role, nav, loc.pathname, loc.search]);

  const wrongRole = role === "ADMIN" && (!me || me.globalRole !== "ADMIN");
  const needsOnboarding =
    role === "EMPLOYER" &&
    !!me &&
    me.globalRole !== "ADMIN" &&
    me.memberships.length === 0;
  // Block (and keep the splash up) until we have a usable `me` for this role
  // and the user doesn't need to be redirected away.
  const blocking = isPending || !me || wrongRole || needsOnboarding;

  return (
    <>
      {blocking ? null : children(me)}
      <BootSplash active={blocking} />
    </>
  );
}
